import { useCallback, useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { getAddressSearch } from "tm-shared/api";
import { validationMessage } from "tm-shared/utils";

import { useAsyncOptions } from "@/hooks";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { RHFAutocomplete } from "@/shared/forms/RHF/RHFAutocomplete";
import {
    setPointCreatePageModal,
    setPointCreatePageOptionsCache,
} from "@/store/slices/ui";
import { NewPointFormValues } from "@/types/form";
import { PointCreateModal } from "@/types/modals";

import { FieldName } from "../CreatePointForm.constants";

interface AddressSectionProps {
    activeMap: Record<FieldName, boolean>;
    resetBelow: (field: FieldName) => void;
}

export const AddressSection = ({
    activeMap,
    resetBelow,
}: AddressSectionProps) => {
    const dispatch = useAppDispatch();

    const { optionCache } = useAppSelector((state) => state.ui.pointCreatePage);

    const { setValue, getValues, control } =
        useFormContext<NewPointFormValues>();

    const address = useWatch<NewPointFormValues>({
        name: "address",
        control,
    });

    const handleAddAddress = useCallback(() => {
        const { region } = getValues();

        const regionOption = optionCache?.region?.find(
            (option) => option.value === region,
        );

        dispatch(
            setPointCreatePageModal({
                type: PointCreateModal.AddAddress,
                payload: {
                    region: regionOption?.label ?? "",
                    regionId: regionOption?.value ?? "",
                },
            }),
        );
    }, [dispatch, getValues, optionCache?.region]);

    const [searchQuery, setSearchQuery] = useState("");

    const { options: fetchedOptions, loading: loadingAddress } =
        useAsyncOptions({
            fetchFn: (signal) => {
                const { region } = getValues();

                return getAddressSearch(
                    {
                        name: searchQuery,
                        areaId: region,
                    },
                    { axiosOverrides: { signal } },
                );
            },
            selectData: (response) => Object.entries(response.result),
            mapToOption: ([value, label]) => ({ label, value }),
            dependencies: [searchQuery],
            enabled:
                ((!searchQuery && !address) || searchQuery.length >= 3) &&
                activeMap.address,
            debounceMs: 500,
        });

    useEffect(() => {
        if (fetchedOptions?.length) {
            dispatch(
                setPointCreatePageOptionsCache({
                    fieldName: "address",
                    options: fetchedOptions,
                }),
            );
        }
    }, [dispatch, fetchedOptions]);

    const handleInputChange = useCallback(
        (value: string) => {
            setSearchQuery(value);
            setValue("address", "");
            resetBelow("address");
        },
        [resetBelow, setValue],
    );

    return (
        <RHFAutocomplete
            name="address"
            options={optionCache.address ?? []}
            label="Адреса"
            rules={{
                required: validationMessage.required,
            }}
            actionOption={{
                label: "Додати адресу",
                onClick: handleAddAddress,
            }}
            allowClear={false}
            disabled={!activeMap.address}
            onAfterChange={() => resetBelow("address")}
            loading={loadingAddress}
            onInputChange={handleInputChange}
            selectProps={{
                size: "large",
            }}
        />
    );
};
